import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { PageLayout } from "@/components/layout/PageLayout";
import { Helmet } from "react-helmet-async";
import { CoinGrid } from "@/components/coins/CoinGrid";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Flame, Search, TrendingUp, Clock } from "lucide-react";
import { useState } from "react";
import { useSearchParams } from "react-router-dom";

type SortType = "market_cap" | "volume_24h" | "price_change_24h" | "created_at";

export default function Trending() {
  const [searchParams] = useSearchParams();
  const initialQuery = searchParams.get("q") || "";
  const [search, setSearch] = useState(initialQuery);
  const [sortBy, setSortBy] = useState<SortType>("market_cap");

  const { data: coins, isLoading } = useQuery({
    queryKey: ["trending-coins", sortBy, search],
    queryFn: async () => {
      let query = supabase.from("coins").select("*");

      if (search) {
        query = query.or(`name.ilike.%${search}%,ticker.ilike.%${search}%`);
      }

      query = query.order(sortBy, { ascending: false }).limit(50);

      const { data, error } = await query;
      if (error) throw error;
      return data ?? [];
    },
  });

  const sortOptions: { id: SortType; label: string; icon: typeof TrendingUp }[] = [
    { id: "market_cap", label: "Market Cap", icon: TrendingUp },
    { id: "volume_24h", label: "Volume", icon: Flame },
    { id: "price_change_24h", label: "% Change", icon: TrendingUp },
    { id: "created_at", label: "Newest", icon: Clock },
  ];

  return (
    <>
      <Helmet>
        <title>Trending Coins - TheMoonRun</title>
        <meta name="description" content="Discover trending meme coins on Solana. Real-time rankings by market cap, volume, and performance." />
      </Helmet>

      <PageLayout>
        {/* Header */}
        <div className="flex items-center gap-4 mb-6">
          <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-2xl bg-gradient-to-br from-orange-500/20 to-red-500/20 flex items-center justify-center">
            <Flame className="w-6 h-6 sm:w-7 sm:h-7 text-orange-500" />
          </div>
          <div>
            <h1 className="text-xl sm:text-2xl font-display font-bold text-foreground">Trending Coins</h1>
            <p className="text-sm text-muted-foreground">Real-time rankings on Solana</p>
          </div>
        </div>

        {/* Search & Sort */}
        <div className="flex flex-col gap-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              placeholder="Search by name or ticker..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-10"
            />
          </div>
          <div className="flex gap-2 flex-wrap">
            {sortOptions.map((option) => (
              <Button
                key={option.id}
                variant={sortBy === option.id ? "default" : "outline"}
                size="sm"
                onClick={() => setSortBy(option.id)}
              >
                <option.icon className="w-4 h-4 mr-1" />
                <span className="hidden sm:inline">{option.label}</span>
                <span className="sm:hidden">{option.label.split(" ")[0]}</span>
              </Button>
            ))}
          </div>
        </div>

        {/* Results */}
        <CoinGrid coins={coins ?? []} isLoading={isLoading} />

        {coins && coins.length === 0 && !isLoading && (
          <div className="text-center py-12">
            <Flame className="w-12 h-12 text-muted-foreground/30 mx-auto mb-3" />
            <p className="text-muted-foreground">No coins found</p>
          </div>
        )}
      </PageLayout>
    </>
  );
}
